<template>
  <div>
    <item-header
      type="simulation"
      :choosable="true"
      :with-dropdown="true"
      :null-option="$t('labels.selectSimulation')"
      :selected-id="openSimulationId"
      @select="selectSimulation"
    />
    <drawer
      :title="selectedImpactMatrixContext.matrixTypeTitle"
      :open="gridDrawerOpen"
      @update:open="gridDrawerOpen = $event"
    >
      <template v-if="selectedImpactMatrix" >
        <cross-impact-matrix :matrix-id="selectedImpactMatrix.id" :key="selectedImpactMatrix.id" class="mt-4" />
  
        <button
          class="button mt-4"
          type="button"
          @click="state.matrixOverlayOpen = true"
        >
          {{ $t("labels.valueTable") }}
        </button>
  
        <cross-impact-matrix-overlay
          :visible="state.matrixOverlayOpen"
          :matrix-id="selectedImpactMatrix.id"
          @close="state.matrixOverlayOpen = false"
        />
      </template>
      <p v-else class="message">
        {{ $t("errors.noImpactMatrix") }}
      </p>

      <input-dropdown
        ref="matrixDropdownInput"
        class="mt-4"
        :options="impactMatrices"
        :value="selectedImpactMatrix"
        :label="$t('labels.impactMatrixProfile')"
        @update:value="selectImpactMatrix"
      >
        <template #default="{ item }">
          {{ getImpactMatrixTitle(item) }}
        </template>
        <template #after-option="{ item }">
          <button class="button-icon options-list__optionextra" type="button" @click="deleteMatrix(item)">
            <lucide-icon name="trash-2" />
          </button>
        </template>
        <template #after-options>
          <button class="options-list__option mt-4" type="button" @click="createImpactMatrix">
            <lucide-icon name="plus" /> {{ $t("actions.createImpactMatrix") }}
          </button>
        </template>
        <template #empty><span></span></template>
      </input-dropdown>
    </drawer>
    <drawer
      v-if="!openSimulation"
      :title="$t('labels.manualStateChange')"
      :open="simulationInfoDrawerOpen"
      @update:open="setDrawerState('simulationInfo', $event)"
    >
      <item-combo-select
        :targets="targets"
        :resources="{}"
        :indices="{}"
        :selection="targetsWithStateChanges"
        :placeholder="$t('actions.selectTargets')"
        @select-single="onStateChangeTargetSelect"
        @choose="onStateChangeTargetsChoose"
      />
      <sortable-list
        :items="targetStateChanges"
        :order="targetStateChangesOrder"
        :sortable="true"
        class="mt-3"
        @update:order="setTargetStateChangeOrder($event)"
      >
        <template #default="{ item }">
          <element-state-change-input
            :change="item"
            class="mb-1"
            @remove="removeTargetStateChange(item)"
          />
        </template>
      </sortable-list>
    </drawer>
    <info-simulation
      v-if="openSimulation"
      :open="simulationInfoDrawerOpen"
      @update:open="setDrawerState('simulationInfo', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, ref } from "vue";
import { ensureInt } from "@/helpers/utility/Type";

import Drawer from "@core/ui/DrawerBlock.vue";
import SortableList from "@core/ui/SortableList.vue";
import ItemHeader from "@editor/components/ItemHeader.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import InfoSimulation from "@editor/views/inspector/components/InfoSimulation.vue";

import CrossImpactMatrix from "@editor/items/impactMatrix/CrossImpactMatrix.vue";
import CrossImpactMatrixOverlay from "@editor/items/impactMatrix/CrossImpactMatrixOverlay.vue";
import ElementStateChangeInput from "@editor/views/inspector/components/ElementStateChangeInput.vue";

import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useDrawerStates } from "@editor/store/editor/drawerStates.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useImpactMatrixStore } from "@editor/items/impactMatrix/impactMatrices.store";
import { getImpactMatrixTitle } from "@editor/items/impactMatrix/impactMatrixFunctions";
import { useImpactMatrix } from "@editor/items/impactMatrix/useImpactMatrix";

import type { TargetItem } from "@editor/items/target/TargetItem";

const targetStore = useTargetStore();
const drawerStates = useDrawerStates();
const inspectorView = useInspectorView();
const impactMatrixStore = useImpactMatrixStore();
const simulationViewStore = useSimulationViewStore();
const state = reactive({
  matrixOverlayOpen: false
});

const matrixDropdownInput = ref<InstanceType<typeof InputDropdown>>();

// ================
// SIMULATION

const openSimulation = computed(() => simulationViewStore.simulation);
const openSimulationId = computed(() => {
  return openSimulation.value ? openSimulation.value.id : undefined;
});

function selectSimulation(simulationId: number | null) {
  simulationViewStore.loadSimulation({ simulationId });
}

// ================
// IMPACT MATRICES

const impactMatrices = computed(() => impactMatrixStore.items);

const selectedImpactMatrix = computed(() => impactMatrixStore.selectedMatrix);

const selectedImpactMatrixContext = useImpactMatrix(() => selectedImpactMatrix.value?.id ?? null);

function selectImpactMatrix(impactMatrix: { id: number }) {
  impactMatrixStore.selectMatrix(impactMatrix.id);
}

async function deleteMatrix(impactMatrix: { id: number }) {
  await impactMatrixStore.delete(impactMatrix);

  matrixDropdownInput.value?.close();
}

async function createImpactMatrix() {
  await impactMatrixStore.new({});

  state.matrixOverlayOpen = true;
  matrixDropdownInput.value?.close();
}

// ================
// STATE CHANGES

const targets = computed(() => targetStore.items);

const targetStateChanges = computed(() => {
  return inspectorView.targetStateChanges;
});
const targetStateChangesOrder = computed(() => {
  return inspectorView.targetStateChangesOrder;
});

const targetsWithStateChanges = computed(() => {
  const targetIds = Object.keys(targetStateChanges.value).map(ensureInt);
  return targetStore.getArrayByIds(targetIds);
});
    

function setTargetStateChangeOrder(order: number[]) {
  inspectorView.set({
    targetStateChangesOrder: order
  });
}

function onStateChangeTargetSelect(target: TargetItem) {
  onStateChangeTargetsChoose([
    ...targetsWithStateChanges.value,
    target
  ]);
}
function onStateChangeTargetsChoose(targets: { id: number }[]) {
  const targetIds = targets.map((e) => e.id);
  inspectorView.setStateChangeTargets(targetIds);
}

function removeTargetStateChange(change: { id: number }) {
  inspectorView.removeTargetStateChange(change.id);
}

// ================
// DRAWER

const gridDrawerOpen = computed({
  get() {
    return inspectorView.gridDrawerOpen;
  },
  set(val: boolean) {
    inspectorView.set({ gridDrawerOpen: val });
  }
});

const simulationInfoDrawerOpen = computed(() => drawerStates.simulationInfo);

function setDrawerState(drawerName: string, open: boolean) {
  drawerStates.set({
    [drawerName]: open
  });
}
</script>

<style></style>
